import type { AllergyModel } from '@ctw/shared/api/fhir/models/allergies';
import { useHistory } from '@ctw/shared/content/resource/history';
import type { HistoryEntryProps } from '@ctw/shared/content/resource/history-entry';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { QUERY_KEY_ALLERGY_HISTORY } from '@ctw/shared/utils/query-keys';
import type { AllergyIntolerance } from 'fhir/r4';
import { capitalize } from 'lodash-es';

export function useAllergiesHistory(allergy: AllergyModel) {
  const { requestContext } = useCTW();
  const builderId = requestContext.builderId ?? '';
  return useHistory({
    resourceType: 'AllergyIntolerance',
    model: allergy,
    queryKey: QUERY_KEY_ALLERGY_HISTORY,
    valuesToDedupeOn,
    getHistoryEntry: (m) => getHistoryEntry(m, builderId),
    clientSideFiltersFQS,
  });
}

function clientSideFiltersFQS(model: AllergyModel, allergies: Array<AllergyIntolerance>) {
  const tokens = model.knownCodings.map((coding) => `${coding.system}|${coding.code}`);
  return allergies.filter((allergy) => {
    const modelTokens = allergy.code?.coding?.map((coding) => `${coding.system}|${coding.code}`);
    // Sometimes data doesn't have known codings, so we also match on the code.text making sure that it is not blank.
    const matchingCodeDisplay = model.codeText && allergy.code?.text === model.codeText;
    const matchingSystemCode = modelTokens?.some((token) => tokens.includes(token));
    return matchingSystemCode || matchingCodeDisplay;
  });
}

function getHistoryEntry(allergy: AllergyModel, builderId: string): HistoryEntryProps {
  const detailData = [
    { label: 'Status', value: allergy.clinicalStatus },
    { label: 'Type', value: capitalize(allergy.type) },
    { label: 'Onset', value: allergy.onset },
    { label: 'Reaction', value: capitalize(allergy.manifestations) },
    { label: 'Severity', value: capitalize(allergy.severity) },
    { label: 'Note', value: allergy.note },
    { label: 'Data Source', value: allergy.getDataSourceDisplay(builderId) },
  ];

  return {
    id: allergy.id,
    model: allergy,
    date: allergy.recordedDate,
    versionId: allergy.versionId,
    title: allergy.patientOrganizationName,
    details: detailData,
  };
}

const valuesToDedupeOn = (allergy: AllergyModel) => [
  allergy.recordedDate,
  allergy.patientOrganizationName,
  allergy.clinicalStatus,
  allergy.onset,
  allergy.manifestations,
  allergy.severity,
  allergy.note,
  allergy.knownCodings.map((coding) => [coding.system, coding.code]),
];
